import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { formatCurrency } from '../../../../utils/formatters';
import type { BcraCentralDeudoresConsulta } from '../../../../types/bcraCentralDeudores';
import {
  filtrarChequesBcraUltimos12Meses,
  sumarImporteChequesBcra,
} from '../../../../types/bcraCentralDeudores';
import BcraCentralDeudoresDialog from '../../../../components/shared/BcraCentralDeudoresDialog';
import type { Info360ChequesDevueltos } from '../types';
import {
  ChipEstadoChequeDevuelto,
  formatInfo360ChequeFecha,
  textoMotivoSeguimiento,
} from '../chequesDevueltosDisplay';
import Info360ChequesDevueltosDialog from './Info360ChequesDevueltosDialog';

interface Props {
  nroCliente: number;
  chequesDevueltos: Info360ChequesDevueltos;
  bcraData: BcraCentralDeudoresConsulta | null;
  bcraLoading?: boolean;
}

const PREVIEW_LIMIT = 5;

const Info360ChequesDevueltosSection: React.FC<Props> = ({
  nroCliente,
  chequesDevueltos,
  bcraData,
  bcraLoading = false,
}) => {
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [bcraModalOpen, setBcraModalOpen] = useState(false);

  const chequesBcra12 = useMemo(
    () => (bcraData ? filtrarChequesBcraUltimos12Meses(bcraData.chequesRechazados) : []),
    [bcraData],
  );
  const importeBcra12 = useMemo(() => sumarImporteChequesBcra(chequesBcra12), [chequesBcra12]);

  const itemsInternos = chequesDevueltos.items ?? [];
  const tieneInternos = chequesDevueltos.cantidad > 0;
  const tieneBcra = chequesBcra12.length > 0;
  const previewItems = itemsInternos.slice(0, PREVIEW_LIMIT);

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          Cheques devueltos — últimos 12 meses
        </Typography>
        {tieneInternos ? (
          <Stack spacing={1.5}>
            <Alert severity={chequesDevueltos.cantidad >= 3 ? 'error' : 'warning'} variant="outlined">
              {chequesDevueltos.cantidad} cheque{chequesDevueltos.cantidad === 1 ? '' : 's'} devuelto
              {chequesDevueltos.cantidad === 1 ? '' : 's'} por un total de {formatCurrency(chequesDevueltos.importeTotal)}.
            </Alert>
            {previewItems.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha rechazo</TableCell>
                      <TableCell>Nº cheque</TableCell>
                      <TableCell align="right">Importe</TableCell>
                      <TableCell>Motivo / seguimiento</TableCell>
                      <TableCell>Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewItems.map((item) => (
                      <TableRow key={item.nroCarga}>
                        <TableCell>{formatInfo360ChequeFecha(item.fechaRechazo)}</TableCell>
                        <TableCell>{item.nroCheque}</TableCell>
                        <TableCell align="right">{formatCurrency(item.importe)}</TableCell>
                        <TableCell>{textoMotivoSeguimiento(item.concepto)}</TableCell>
                        <TableCell>
                          <ChipEstadoChequeDevuelto item={{ concepto: item.concepto, rechazado: 1 }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
            {itemsInternos.length > PREVIEW_LIMIT ? (
              <Typography variant="caption" color="text.secondary">
                Mostrando {PREVIEW_LIMIT} de {itemsInternos.length} cheques.
              </Typography>
            ) : null}
            <Button
              variant="text"
              size="small"
              onClick={() => setDetalleOpen(true)}
              sx={{ alignSelf: 'flex-start', textTransform: 'none', px: 0 }}
            >
              Ver detalle
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircleOutlineIcon color="success" fontSize="small" />
            <Typography variant="body2" color="text.secondary">
              No registramos cheques devueltos de este cliente en los últimos 12 meses.
            </Typography>
          </Stack>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          Cheques rechazados BCRA — últimos 12 meses
        </Typography>
        {bcraLoading && !bcraData ? (
          <Typography variant="body2" color="text.secondary">
            Consultando Central de Deudores…
          </Typography>
        ) : !bcraData ? (
          <Typography variant="body2" color="text.secondary">
            Sin consulta BCRA disponible para este cliente.
          </Typography>
        ) : tieneBcra ? (
          <Stack spacing={1.5}>
            <Alert severity={chequesBcra12.length >= 3 ? 'error' : 'warning'} variant="outlined">
              {chequesBcra12.length} cheque{chequesBcra12.length === 1 ? '' : 's'} rechazado
              {chequesBcra12.length === 1 ? '' : 's'} según BCRA por un total de {formatCurrency(importeBcra12)}.
            </Alert>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nº cheque</TableCell>
                    <TableCell>Fecha rechazo</TableCell>
                    <TableCell align="right">Monto</TableCell>
                    <TableCell>Causal</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chequesBcra12.slice(0, 5).map((c, idx) => (
                    <TableRow key={`${c.nroCheque}-${idx}`}>
                      <TableCell>{c.nroCheque}</TableCell>
                      <TableCell>{formatInfo360ChequeFecha(c.fechaRechazo)}</TableCell>
                      <TableCell align="right">{formatCurrency(c.monto)}</TableCell>
                      <TableCell>{c.causal || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {chequesBcra12.length > 5 ? (
              <Typography variant="caption" color="text.secondary">
                Mostrando 5 de {chequesBcra12.length} cheques.
              </Typography>
            ) : null}
            <Box>
              <Button
                variant="text"
                size="small"
                onClick={() => setBcraModalOpen(true)}
                sx={{ textTransform: 'none', px: 0 }}
              >
                Ver en informe BCRA
              </Button>
            </Box>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircleOutlineIcon color="success" fontSize="small" />
            <Typography variant="body2" color="text.secondary">
              Sin cheques rechazados BCRA en los últimos 12 meses.
            </Typography>
          </Stack>
        )}
      </Paper>

      <Info360ChequesDevueltosDialog
        open={detalleOpen}
        onClose={() => setDetalleOpen(false)}
        nroCliente={nroCliente}
        resumenItems={chequesDevueltos.items}
      />

      <BcraCentralDeudoresDialog
        open={bcraModalOpen}
        onClose={() => setBcraModalOpen(false)}
        data={bcraData}
      />
    </>
  );
};

export default Info360ChequesDevueltosSection;
